import { useCallback, useMemo, useReducer } from 'react';
import { match } from 'ts-pattern';
import {
  JsonLocalStorageService,
  LocalStorageService,
} from '../api/local-storage-service';
import { HasDependencies, hashString } from '../api/types';
import { Domains } from '../constants';
import {
  GlobalDisplayAction,
  GlobalDisplayHandle,
  GlobalDisplayState,
  GlobalResetHandle,
} from './types';

export const useMemoWithDeps = <T,>(
  debugString: string,
  toMemo: () => T,
  deps: (string | undefined)[],
  hasDeps?: HasDependencies[]
) => {
  const depsHash = hashString(hasDeps?.flatMap((d) => d.getDeps()) ?? []);
  return useMemo<T>(() => {
    console.debug(`[useMemoWithDeps] compute value - ${debugString}`, [
      ...deps,
      depsHash,
    ]);
    return toMemo();
  }, [...deps, depsHash]);
};

const initialState = () => ({
  enablePercentage: true,
  showPercentage: false,
  enableStack: true,
  showStack: false,
  enableGridLines: true,
  showGridLines: false,
  enableLabels: true,
  showLabels: false,
  enableLegend: true,
  showLegend: false,
});

export const useGlobalDisplayHandleWithPersistence = (
  domain: Domains
): GlobalDisplayHandle => {
  const persistenceKey = `${domain}-display-v1`;
  const localStorageService = new JsonLocalStorageService<GlobalDisplayState>(
    persistenceKey
  );
  const storedItems = localStorageService.get();
  const state = storedItems ?? initialState();
  return useReducer(
    reducerWithPersistentWrapper(reducer, localStorageService),
    state
  );
};

export const useGlobalDisplayHandle = (): GlobalDisplayHandle => {
  return useReducer(reducer, initialState());
};

export const reducerWithPersistentWrapper = <
  S,
  State extends Record<keyof S, unknown>,
  Action extends { type: string },
>(
  underlyingReducer: (state: State, action: Action) => State,
  localStorageService: LocalStorageService<State>
): ((state: State, action: Action) => State) => {
  return (state: State, action: Action): State => {
    const newstate = underlyingReducer(state, action);
    // TODO: think whether we should store a smaller version of the state so that we can update keys without breaking the app for existing users
    localStorageService.set(newstate);
    return newstate;
  };
};

const reducer = (
  state: GlobalDisplayState,
  action: GlobalDisplayAction
): GlobalDisplayState => {
  return match(action.type)
    .with('toggle-percentage', () => ({
      ...state,
      showPercentage: !state.showPercentage,
      enableStack: state.showPercentage,
    }))
    .with('toggle-stack', () => ({
      ...state,
      showStack: !state.showStack,
      enablePercentage: state.showStack,
    }))
    .with('toggle-gridlines', () => ({
      ...state,
      showGridLines: !state.showGridLines,
    }))
    .with('toggle-labels', () => ({
      ...state,
      showLabels: !state.showLabels,
    }))
    .with('toggle-legend', () => ({
      ...state,
      showLegend: !state.showLegend,
    }))
    .with('reset', () => ({
      enablePercentage: true,
      showPercentage: false,
      enableStack: true,
      showStack: false,
      enableGridLines: true,
      showGridLines: false,
      enableLabels: true,
      showLabels: false,
      enableLegend: true,
      showLegend: false,
    }))
    .exhaustive();
};

export const useGlobalResetHandle = (
  resets: Array<() => void>
): GlobalResetHandle => {
  const reset = useCallback(() => {
    resets.map((r) => r());
  }, [resets]);
  return { reset };
};
